import type { ReactNode } from 'react'
import { useLingui } from '@lingui/react/macro'
import {
  BriefcaseIcon,
  BuildingIcon,
  ClipboardCheckIcon,
  EyeIcon,
  LanguagesIcon,
  MailIcon,
  PlugIcon,
  MapPinIcon,
  SearchIcon,
  SettingsIcon,
  ShieldIcon,
  TargetIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
} from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useSessionUser } from '@/hooks/use-session'
import { useModalSlotPending } from '@/components/custom/modal-slot'
import {
  HORIZONTAL_CONTROL_CLASS,
  HORIZONTAL_LABEL_CLASS,
  HORIZONTAL_ROW_CLASS,
} from '@/components/custom/form/horizontal-field'
import {
  ButtonSkeleton,
  DialogSkeleton,
  DialogTitleSkeleton,
  FieldRowSkeleton,
  FieldsetSkeleton,
  FormSkeleton,
  MessageWithSkeleton,
  SKELETON_VALUE,
  SubmitRowSkeleton,
  TextSkeleton,
} from '@/components/custom/dialog-skeleton'
import { useCookieDescription } from './cookie-banner'
import { WELCOME_DIALOG_CLASS, WELCOME_FOOTER_CLASS, WelcomeContent } from '../companies/welcome-content'
import {
  SAMPLE_NOTICE_DIALOG_CLASS,
  SAMPLE_NOTICE_FOOTER_CLASS,
  SampleNoticeContent,
} from '../companies/sample-notice-content'
import { DescribeDialogPending as DescribeFrame, type Indicator } from '../offers/describe-dialog-pending'
import { AssistantSurface } from '../assistant/assistant-surface'
import { AssistantSkeleton } from '../assistant/assistant-skeleton'

/**
 * Every modal slot route's pendingComponent: the dialog it opens, in its real shape, while its chunk (and whatever
 * it suspends on) is on the wire. Each mirrors its dialog's markup — width, title, legends, labels and buttons —
 * and draws as a skeleton only what is not known yet. The rule and how the geometry is pinned:
 * docs/notes/app/loading-states.md.
 */

// MARK: Forms

// The value a title interpolates (`Edit job: ${jobName}`) stands in as a skeleton; the variable keeps the source's
// name, because the message id is built from it.
const jobName = SKELETON_VALUE
const offerName = SKELETON_VALUE
const teamName = SKELETON_VALUE
const employeeName = SKELETON_VALUE
const memberName = SKELETON_VALUE
const applicantName = SKELETON_VALUE

function FormDialogPending(props: { label: string; title: ReactNode; children: ReactNode }) {
  return (
    <DialogSkeleton label={props.label} className="sm:max-w-xl">
      <DialogTitleSkeleton>{props.title}</DialogTitleSkeleton>
      <FormSkeleton>{props.children}</FormSkeleton>
    </DialogSkeleton>
  )
}

function JobFields(props: { kind: 'job' | 'offer' }) {
  const { t } = useLingui()
  const peopleInCharge = t`With people under charge?`
  return (
    <>
      <FieldsetSkeleton icon={BriefcaseIcon} legend={props.kind === 'job' ? t`Job details` : t`Offer details`}>
        <FieldRowSkeleton label={props.kind === 'job' ? t`Job name` : t`Offer name`} required />
        <FieldRowSkeleton label={t`Professional level`} required />
        <FieldRowSkeleton label={t`Function`} required />
        <FieldRowSkeleton label={t`Sector`} required />
        <FieldRowSkeleton label={t`Studies`} required />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={MapPinIcon} legend={t`Location`}>
        <FieldRowSkeleton label={t`Country`} required />
        <FieldRowSkeleton label={t`State / Province`} required />
        <FieldRowSkeleton label={t`City`} required />
        <FieldRowSkeleton label={t`Postal code`} required />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={SettingsIcon} legend={t`Settings`}>
        {/* The job form's is a form field (an `h-8` label row); the offer form's is a bare switch beside its label. */}
        {props.kind === 'job' ? (
          <FieldRowSkeleton label={peopleInCharge} control="switch" />
        ) : (
          <SwitchRowSkeleton label={peopleInCharge} labelClassName="w-48 shrink-0 text-sm font-medium" />
        )}
      </FieldsetSkeleton>
    </>
  )
}

/** The job form's body under its title: also the form's own fallback while an edited job loads (job-form.tsx). */
export function JobFormSkeleton(props: { editing: boolean }) {
  const { t } = useLingui()
  return (
    <>
      <DialogTitleSkeleton>
        {props.editing ? <MessageWithSkeleton message={t`Edit job: ${jobName}`} /> : t`New job`}
      </DialogTitleSkeleton>
      <FormSkeleton>
        <JobFields kind="job" />
        <SubmitRowSkeleton label={props.editing ? t`Save changes` : t`Add job`} />
      </FormSkeleton>
    </>
  )
}

/** The offer form's body under its title: also the form's own fallback while an edited offer loads (offer-form.tsx). */
export function OfferFormSkeleton(props: { editing: boolean }) {
  const { t } = useLingui()
  return (
    <>
      <DialogTitleSkeleton>
        {props.editing ? <MessageWithSkeleton message={t`Edit offer: ${offerName}`} /> : t`New offer`}
      </DialogTitleSkeleton>
      <FormSkeleton>
        <JobFields kind="offer" />
        <SubmitRowSkeleton label={props.editing ? t`Save changes` : t`Add offer`} />
      </FormSkeleton>
    </>
  )
}

export function NewJobDialogPending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`New job`} className="sm:max-w-xl">
      <JobFormSkeleton editing={false} />
    </DialogSkeleton>
  )
}

export function EditJobDialogPending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`Edit job: ${jobName}`} className="sm:max-w-xl">
      <JobFormSkeleton editing />
    </DialogSkeleton>
  )
}

export function NewOfferDialogPending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`New offer`} className="sm:max-w-xl">
      <OfferFormSkeleton editing={false} />
    </DialogSkeleton>
  )
}

export function EditOfferDialogPending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`Edit offer: ${offerName}`} className="sm:max-w-xl">
      <OfferFormSkeleton editing />
    </DialogSkeleton>
  )
}

function TeamFormPending(props: { editing: boolean }) {
  const { t } = useLingui()
  const title = props.editing ? t`Edit team: ${teamName}` : t`New team`
  return (
    <FormDialogPending label={title} title={<MessageWithSkeleton message={title} />}>
      <FieldsetSkeleton icon={UsersIcon} legend={t`Team details`}>
        <FieldRowSkeleton label={t`Name of the team`} required />
        <FieldRowSkeleton label={t`Professional level`} required />
        <FieldRowSkeleton label={t`Function`} required />
        <FieldRowSkeleton label={t`Sector`} required />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={MapPinIcon} legend={t`Location`}>
        <FieldRowSkeleton label={t`Country`} required />
        <FieldRowSkeleton label={t`State / Province`} required />
        <FieldRowSkeleton label={t`City`} required />
        <FieldRowSkeleton label={t`Postal Code`} />
      </FieldsetSkeleton>
      <SubmitRowSkeleton label={props.editing ? t`Save changes` : t`Add team`} />
    </FormDialogPending>
  )
}

export function NewTeamDialogPending() {
  return <TeamFormPending editing={false} />
}

export function EditTeamDialogPending() {
  return <TeamFormPending editing />
}

function EmployeeFormPending(props: {
  title: ReactNode
  label: string
  submit: string
  /** A new employee's form ends with the invitation note (not a candidate's conversion, not an edit). */
  invitationNote?: boolean
}) {
  const { t } = useLingui()
  return (
    <FormDialogPending label={props.label} title={props.title}>
      <FieldsetSkeleton icon={UserIcon} legend={t`Personal information`} className="pb-10">
        <FieldRowSkeleton label={t`Photo`} control="photo" />
        <FieldRowSkeleton label={t`Email`} required />
        <FieldRowSkeleton label={t`First name`} required />
        <FieldRowSkeleton label={t`Last name`} required />
        <FieldRowSkeleton label={t`Sex`} required />
        <FieldRowSkeleton label={t`Date of birth`} required />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={BuildingIcon} legend={t`Organization`} className="pb-10">
        <FieldRowSkeleton label={t`Job`} required />
        <FieldRowSkeleton label={t`Teams`} />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={BriefcaseIcon} legend={t`Professional details`} className="pb-10">
        <FieldRowSkeleton label={t`Professional level`} required />
        <FieldRowSkeleton label={t`Function`} required />
        <FieldRowSkeleton label={t`Sector`} required />
        <FieldRowSkeleton label={t`Studies`} required />
        <FieldRowSkeleton label={t`With people under charge?`} control="switch" />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={MapPinIcon} legend={t`Location`} className="pb-10">
        <FieldRowSkeleton label={t`Country`} required />
        <FieldRowSkeleton label={t`Province / State`} required />
        <FieldRowSkeleton label={t`City`} required />
        <FieldRowSkeleton label={t`Postal code`} />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={ShieldIcon} legend={t`Permissions`} className="pb-10">
        <FieldRowSkeleton label={t`Access level`} required />
        <FieldRowSkeleton label={t`Report type`} required />
        <FieldRowSkeleton label={t`Retake test`} control="switch" />
      </FieldsetSkeleton>
      <SubmitRowSkeleton label={props.submit} />
      {props.invitationNote && (
        <p className="text-muted-foreground pt-3 text-xs">
          {t`This employee will receive an invitation to register and complete their Psychographic Profile test. Once completed, their results will be added automatically.`}
        </p>
      )}
    </FormDialogPending>
  )
}

/** A label above its input, as the self-add gate stacks them. */
function StackedFieldSkeleton(props: { label: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-2 text-sm leading-none font-medium select-none">{props.label}</span>
      <Skeleton className="h-8 w-full rounded-lg" />
    </div>
  )
}

/** The first "+ Add" of a company that has not added itself yet opens a gate instead of the form (edit-employee.tsx). */
function SelfAddGatePending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`First, add yourself`} className="sm:max-w-md">
      <DialogTitleSkeleton
        description={t`To do so, enter your employee email.`}
      >{t`First, add yourself`}</DialogTitleSkeleton>
      <div className="flex flex-col gap-3 py-2">
        <StackedFieldSkeleton label={t`Email`} />
        <p className="text-muted-foreground text-sm">
          {t`In your registration form, the Access level and Report type fields must be exactly these:`}
        </p>
        <StackedFieldSkeleton label={t`Access level`} />
        <StackedFieldSkeleton label={t`Report type`} />
      </div>
      <div className="-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl p-4 sm:flex-row sm:justify-end">
        <ButtonSkeleton label={t`Fill in form`} />
      </div>
    </DialogSkeleton>
  )
}

export function NewEmployeeDialogPending() {
  const { t } = useLingui()
  if (useSessionUser()?.canSelfAddAdmin) return <SelfAddGatePending />
  return <EmployeeFormPending label={t`New employee`} title={t`New employee`} submit={t`Add employee`} invitationNote />
}

export function EditEmployeeDialogPending() {
  const { t } = useLingui()
  return (
    <EmployeeFormPending
      label={t`Edit employee: ${employeeName}`}
      title={<MessageWithSkeleton message={t`Edit employee: ${employeeName}`} />}
      submit={t`Save changes`}
    />
  )
}

export function HireApplicantDialogPending() {
  const { t } = useLingui()
  return (
    <EmployeeFormPending
      label={t`Add ${employeeName} to the employee list`}
      title={<MessageWithSkeleton message={t`Add ${employeeName} to the employee list`} />}
      submit={t`Add employee`}
    />
  )
}

function OrgFormPending(props: { editing: boolean }) {
  const { t } = useLingui()
  const title = props.editing ? t`Edit administration` : t`New administration`
  return (
    <FormDialogPending label={title} title={title}>
      <FieldsetSkeleton icon={BuildingIcon} legend={t`Administration details`}>
        <FieldRowSkeleton label={t`Name (Spanish)`} required />
        <FieldRowSkeleton label={t`Name (Catalan)`} required />
        <FieldRowSkeleton label={t`Name (English)`} required />
      </FieldsetSkeleton>
      <SubmitRowSkeleton label={props.editing ? t`Save changes` : t`Create an administration`} />
    </FormDialogPending>
  )
}

export function NewOrgDialogPending() {
  return <OrgFormPending editing={false} />
}

export function EditOrgDialogPending() {
  return <OrgFormPending editing />
}

/**
 * A switch row whose label is plain text beside it (the group's competencies, a member's permissions). Those labels
 * pass `text-sm` to `<Label>`, which merges away its `leading-none`: they sit on `text-sm`'s own 20px line.
 */
function SwitchRowSkeleton(props: { label: string; labelClassName: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className={props.labelClassName}>{props.label}</span>
      <Skeleton className="h-[18.4px] w-8 shrink-0 rounded-full" />
    </div>
  )
}

function GroupFormPending(props: { editing: boolean }) {
  const { t } = useLingui()
  const title = props.editing ? t`Edit functional group` : t`New functional group`
  return (
    <FormDialogPending label={title} title={title}>
      <FieldsetSkeleton icon={UsersIcon} legend={t`Group details`}>
        <FieldRowSkeleton label={t`Name (Spanish)`} required />
        <FieldRowSkeleton label={t`Name (Catalan)`} required />
        <FieldRowSkeleton label={t`Name (English)`} required />
        <FieldRowSkeleton label={t`Password`} required />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={TargetIcon} legend={t`Competencies to evaluate`}>
        <SwitchRowSkeleton label={t`Basic / Attitudinals`} labelClassName="w-48 shrink-0 text-sm font-medium" />
        <SwitchRowSkeleton label={t`Professional / Functional`} labelClassName="w-48 shrink-0 text-sm font-medium" />
        <SwitchRowSkeleton label={t`Transversal`} labelClassName="w-48 shrink-0 text-sm font-medium" />
        <SwitchRowSkeleton label={t`Managerial`} labelClassName="w-48 shrink-0 text-sm font-medium" />
      </FieldsetSkeleton>
      <div className="flex min-w-0 items-center gap-2">
        <ButtonSkeleton label={props.editing ? t`Save changes` : t`Create Group`} />
      </div>
    </FormDialogPending>
  )
}

export function NewGroupDialogPending() {
  return <GroupFormPending editing={false} />
}

export function EditGroupDialogPending() {
  return <GroupFormPending editing />
}

/** A team member's permissions: the switches are the member's current values, so they are what is unknown. */
export function MemberDialogPending() {
  const { t } = useLingui()
  return (
    <FormDialogPending
      label={t`Permissions of: ${memberName}`}
      title={<MessageWithSkeleton message={t`Permissions of: ${memberName}`} />}
    >
      <FieldsetSkeleton icon={EyeIcon} legend={t`Visibility`}>
        <SwitchRowSkeleton
          label={t`See the team's self-assessment and the one of its members`}
          labelClassName="min-w-0 flex-1 text-sm"
        />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={ClipboardCheckIcon} legend={t`Evaluation`}>
        <SwitchRowSkeleton
          label={t`Evaluate other members and see the results`}
          labelClassName="min-w-0 flex-1 text-sm"
        />
        <SwitchRowSkeleton label={t`Reevaluate the members once evaluated`} labelClassName="min-w-0 flex-1 text-sm" />
      </FieldsetSkeleton>
      <div className="flex min-w-0">
        <ButtonSkeleton label={t`Update`} />
      </div>
    </FormDialogPending>
  )
}

/** A read-only form row: the label, and the value as a line of text. */
function ReadOnlyRowSkeleton(props: { label: string }) {
  return (
    <div className={HORIZONTAL_ROW_CLASS}>
      <div className={cn('flex h-5 items-center', HORIZONTAL_LABEL_CLASS)}>
        <span className="text-sm leading-none font-medium">{props.label}</span>
      </div>
      <div className={cn('flex h-8 items-center', HORIZONTAL_CONTROL_CLASS)}>
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  )
}

/**
 * The read-only photo row with no photo: beside its label at every width, as the upload field draws it, with the
 * "no image" line wrapping as the real one does in the narrow column a phone leaves it.
 */
function ReadOnlyPhotoRowSkeleton(props: { label: string }) {
  const { t } = useLingui()
  return (
    <div className="flex min-w-0 flex-1 items-start gap-3">
      <div className="flex h-8 w-48 shrink-0 items-center">
        <span className="text-sm leading-none font-medium">{props.label}</span>
      </div>
      <p className="min-w-0 flex-1 text-sm">
        <TextSkeleton>{t`No image uploaded`}</TextSkeleton>
      </p>
    </div>
  )
}

/** A candidate's profile, read-only: the values are the candidate's, the labels are the form's. */
export function ApplicantDialogPending() {
  const { t } = useLingui()
  return (
    <FormDialogPending
      label={t`Profile data: ${applicantName}`}
      title={<MessageWithSkeleton message={t`Profile data: ${applicantName}`} />}
    >
      <FieldsetSkeleton icon={UserIcon} legend={t`Personal information`} className="pb-10">
        <ReadOnlyPhotoRowSkeleton label={t`Photo`} />
        <ReadOnlyRowSkeleton label={t`First name`} />
        <ReadOnlyRowSkeleton label={t`Last name`} />
        <ReadOnlyRowSkeleton label={t`Sex`} />
        <ReadOnlyRowSkeleton label={t`Date of birth`} />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={BriefcaseIcon} legend={t`Professional details`} className="pb-10">
        <ReadOnlyRowSkeleton label={t`Job`} />
        <ReadOnlyRowSkeleton label={t`Professional level`} />
        <ReadOnlyRowSkeleton label={t`Function`} />
        <ReadOnlyRowSkeleton label={t`Sector`} />
        <ReadOnlyRowSkeleton label={t`Studies`} />
        {/* A switch, read-only: beside its label at every width, as the switch field draws it. */}
        <FieldRowSkeleton label={t`With people under charge?`} control="switch" />
      </FieldsetSkeleton>
      <FieldsetSkeleton icon={MapPinIcon} legend={t`Location`} className="pb-10">
        <ReadOnlyRowSkeleton label={t`Country`} />
        <ReadOnlyRowSkeleton label={t`Province / State`} />
        <ReadOnlyRowSkeleton label={t`City`} />
        <ReadOnlyRowSkeleton label={t`Postal code`} />
      </FieldsetSkeleton>
      <div className="flex min-w-0 gap-4">
        <ButtonSkeleton label={t`Accept`} />
      </div>
    </FormDialogPending>
  )
}

// MARK: The other dialogs

export function InviteDialogPending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`Invite Applicants`} className="sm:max-w-[480px]">
      <div className="flex flex-col gap-6">
        <DialogTitleSkeleton>
          <span className="flex items-center gap-2">
            <UserPlusIcon className="size-5" />
            {t`Invite Applicants`}
            {/* The info popover's trigger, which is hidden below `sm`. */}
            <span className="size-7 max-sm:hidden" />
          </span>
        </DialogTitleSkeleton>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <span className="flex items-center gap-2 text-sm leading-none font-medium">
              <MailIcon className="size-4" />
              {t`Email Addresses`}
            </span>
            <div className="flex gap-2">
              <Skeleton className="h-9 flex-1 rounded-md" />
              <ButtonSkeleton label={t`Add`} className="h-9 rounded-[min(var(--radius-md),12px)] text-[0.8rem]" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-[18.4px] w-8 shrink-0 rounded-full" />
            <span className="text-muted-foreground text-sm">
              {t`Receive email notifications when questionnaires have been completed.`}
            </span>
          </div>
        </div>
        <div className="-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl p-4 sm:flex-row sm:justify-end">
          <ButtonSkeleton label={t`Cancel`} />
          <ButtonSkeleton label={t`Send Invitations`} iconStart />
        </div>
      </div>
    </DialogSkeleton>
  )
}

/** One of the import hub's option cards: its icon is the one box whose content is drawn by the dialog. */
function HubCardSkeleton(props: { title: string; description: string; dropZone?: boolean }) {
  return (
    <div
      className={cn(
        'flex w-full items-start gap-3 rounded-xl p-4',
        props.dropZone ? 'border-2 border-dashed' : 'border',
      )}
    >
      <Skeleton className="size-11 shrink-0 rounded-lg" />
      <div className="flex min-w-0 flex-1 flex-col gap-1 pt-0.5">
        <h3 className="text-sm leading-tight font-semibold">{props.title}</h3>
        <p className="text-muted-foreground text-xs leading-relaxed">{props.description}</p>
      </div>
    </div>
  )
}

export function ImportEmployeesDialogPending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`Import & export employees`} className="sm:max-w-xl">
      <DialogTitleSkeleton
        description={t`Use an Excel workbook to add or update employees, jobs and teams in bulk. Pick an option below.`}
      >
        {t`Import & export employees`}
      </DialogTitleSkeleton>
      <div className="flex flex-col gap-3">
        <HubCardSkeleton
          title={t`Download blank template`}
          description={t`Get an empty Excel file with all sheets, headers, dropdowns and a built-in Instructions page. Fill it yourself — or hand it to an AI assistant with the instructions sheet as context — and upload the completed file back here. Best for the first bulk load.`}
        />
        <HubCardSkeleton
          dropZone
          title={t`Import filled template`}
          description={t`Drop the filled .xlsx file here or click to pick one. Jobs and teams that don't exist yet will be created and linked by name; employees matched by email are updated, otherwise created. Rows with errors are skipped.`}
        />
        <HubCardSkeleton
          title={t`Export current employees`}
          description={t`Download the same template pre-filled with your existing employees. Edit cells to update data, add new rows for new people, and upload the file back. Existing employees are matched by email and updated in place.`}
        />
      </div>
    </DialogSkeleton>
  )
}

/** The collapsed rail's search: the input, then the rows the rail has loaded. */
function FindDialogPending(props: { label: string; placeholder: string }) {
  return (
    <DialogSkeleton
      label={props.label}
      className="mt-[calc(100dvh/3-1rem)] overflow-hidden rounded-xl! p-0"
      showCloseButton={false}
    >
      <div className="bg-popover flex flex-col gap-1 rounded-xl! p-1">
        <div className="p-1 pb-0">
          <div className="bg-input/30 flex h-8 items-center gap-2 rounded-lg px-2">
            <SearchIcon className="size-4 shrink-0 opacity-50" />
            <span className="text-muted-foreground text-sm">{props.placeholder}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-sm px-2 py-1.5">
          <Skeleton className="size-6 shrink-0 rounded-md" />
          <Skeleton className="h-4 w-40" />
        </div>
      </div>
    </DialogSkeleton>
  )
}

export function FindJobsDialogPending() {
  const { t } = useLingui()
  return <FindDialogPending label={t`Search jobs`} placeholder={t`Search jobs...`} />
}

export function FindOffersDialogPending() {
  const { t } = useLingui()
  return <FindDialogPending label={t`Search offers`} placeholder={t`Search offers`} />
}

export function FindTeamsDialogPending() {
  const { t } = useLingui()
  return <FindDialogPending label={t`Search Teams`} placeholder={t`Search Teams`} />
}

export function FindGroupsDialogPending() {
  const { t } = useLingui()
  return <FindDialogPending label={t`Search groups`} placeholder={t`Search groups...`} />
}

/** One cookie category: its name, its switch (the viewer's choice), and its explanation. */
function CookieCategorySkeleton(props: { name: string; text: string }) {
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <span className="font-medium">{props.name}</span>
        <Skeleton className="h-[18.4px] w-8 shrink-0 rounded-full" />
      </div>
      <p className="text-muted-foreground text-sm">{props.text}</p>
    </>
  )
}

/** The cookie preferences: all static text, so only the controls are skeletons. */
export function CookiesDialogPending() {
  const { t } = useLingui()
  const description = useCookieDescription()
  return (
    <DialogSkeleton label={t`Cookie preferences`} className="flex flex-col gap-3 sm:max-w-lg">
      <h2>{t`Cookie preferences`}</h2>
      <p className="text-muted-foreground text-sm">
        {description} <span className="underline">{t`Cookies policy`}</span>
      </p>
      <CookieCategorySkeleton
        name={t`Necessary`}
        text={t`These cookies are needed for the site to work — signing you in, keeping your session open, remembering your language and theme, and storing the choice you make here — so they cannot be switched off. You can set your browser to block them or to warn you about them, but parts of the site will then stop working properly. They are not used to build an advertising profile of you.`}
      />
      <CookieCategorySkeleton
        name={t`Statistics`}
        text={t`These cookies let us count visits and traffic sources so we can measure and improve how the site performs. They tell us which pages are visited most and least, and how people move through the site. We use Google Analytics for this, and the reports we see are aggregated. If you do not allow these cookies, we will not know that you visited the site.`}
      />
      <CookieCategorySkeleton
        name={t`Marketing`}
        text={t`These cookies may be set across the site by our advertising partners. They can be used to build a profile of your interests and show you relevant ads on other sites. They do not store your name or contact details directly, but rely on a unique identifier for your browser and device. If you do not allow these cookies, the advertising you see will be less relevant to you.`}
      />
      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
        <ButtonSkeleton label={t`Reject all`} />
        <ButtonSkeleton label={t`Save preferences`} />
        <ButtonSkeleton label={t`Accept all`} />
      </div>
    </DialogSkeleton>
  )
}

/** The first-visit welcome: its cards rise in one by one anyway, so they paint as their boxes (welcome-content.tsx). */
export function CompanyWelcomeDialogPending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`We welcome you to D'Anchiano`} showCloseButton={false} className={WELCOME_DIALOG_CLASS}>
      <WelcomeContent
        skeleton
        heading={
          <div className="text-center">
            <h2 className="text-2xl font-semibold">{t`We welcome you to D'Anchiano`}</h2>
            <p className="text-muted-foreground mt-1.5 text-base">{t`Set up your account in a clear and simple way`}</p>
          </div>
        }
      />
      <div className={WELCOME_FOOTER_CLASS}>
        <Skeleton className="h-12 w-full rounded-lg" />
        <div className="flex h-12 items-center justify-center">
          <TextSkeleton className="text-sm font-medium">{t`Don't show again`}</TextSkeleton>
        </div>
        <p className="text-muted-foreground text-center text-xs">{t`You can find it again in Settings > Welcome guide.`}</p>
      </div>
    </DialogSkeleton>
  )
}

export function SampleNoticeDialogPending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`Sample profiles`} showCloseButton={false} className={SAMPLE_NOTICE_DIALOG_CLASS}>
      <SampleNoticeContent />
      <div className={SAMPLE_NOTICE_FOOTER_CLASS}>
        <Skeleton className="h-12 w-full rounded-lg" />
      </div>
    </DialogSkeleton>
  )
}

export function LanguageDialogPending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`Change language`} className="flex flex-col gap-2 sm:max-w-sm">
      <DialogTitleSkeleton description={t`Select your preferred language`}>
        <span className="flex items-center gap-2">
          <LanguagesIcon className="text-primary size-5" />
          {t`Change language`}
        </span>
      </DialogTitleSkeleton>
      <div className="flex flex-col gap-2">
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-[52px] w-full rounded-xl" />
        ))}
      </div>
    </DialogSkeleton>
  )
}

/** Public or private: which one is on is the viewer's setting, so neither is drawn selected. */
/** `selected` reserves the check the dialog draws on the chosen option, which narrows its text on a phone. */
function VisibilityOptionSkeleton(props: { title: string; text: string; selected: boolean }) {
  return (
    <div className="bg-muted/30 flex items-center gap-4 rounded-xl border-2 border-transparent p-4">
      <Skeleton className="size-12 shrink-0 rounded-full" />
      <div className="flex-1">
        <p className="text-muted-foreground font-semibold">{props.title}</p>
        <p className="text-muted-foreground text-xs">{props.text}</p>
      </div>
      {props.selected && <Skeleton className="size-6 shrink-0 rounded-full" />}
    </div>
  )
}

export function VisibilityDialogPending() {
  const { t } = useLingui()
  // The session knows which option is chosen, as the dialog reads it (profile-visibility-dialog.tsx).
  const visible = !!useSessionUser()?.visibility
  return (
    <DialogSkeleton label={t`Profile Visibility`} className="flex flex-col gap-2 sm:max-w-[480px]">
      <DialogTitleSkeleton description={t`Decide who can see and compare your profile`}>
        <span className="flex items-center gap-2">
          <ShieldIcon className="text-primary size-5" />
          {t`Profile Visibility`}
        </span>
      </DialogTitleSkeleton>
      <div className="flex flex-col gap-2">
        <VisibilityOptionSkeleton
          title={t`Public Profile`}
          text={t`Anyone will be able to view and compare your profile`}
          selected={visible}
        />
        <VisibilityOptionSkeleton
          title={t`Private Profile`}
          text={t`Only the companies you select will be able to view and compare your profile`}
          selected={!visible}
        />
      </div>
    </DialogSkeleton>
  )
}

/** The describe wizard: one indicator (`/describe/values`) or the three. */
export function DescribeJobDialogPending() {
  const { params } = useModalSlotPending()
  return <DescribeFrame indicator={params.indicator as Indicator | undefined} />
}

/** The assistant's expanded view: its header and composer are static; the conversation lands in the empty middle. */
export function AssistantPending() {
  const { t } = useLingui()
  const { open, close } = useModalSlotPending()
  return (
    <AssistantSurface open={open} onClose={close} label={t`Assistant`} busy>
      <AssistantSkeleton wide onClose={close} />
    </AssistantSurface>
  )
}

/** The connected AI apps: its title and description as text, a box per row of a short list, the URL field. */
export function AiAppsDialogPending() {
  const { t } = useLingui()
  return (
    <DialogSkeleton label={t`Connected AI apps`} className="flex flex-col gap-4 sm:max-w-lg">
      <DialogTitleSkeleton
        description={t`AI apps such as Claude or ChatGPT can use your D’Anchiano data from their own chat, with what you can see here. They receive real names and scores.`}
      >
        <span className="flex items-center gap-2">
          <PlugIcon className="text-primary size-5" />
          {t`Connected AI apps`}
        </span>
      </DialogTitleSkeleton>
      <Skeleton className="h-[60px] w-full rounded-xl" />
      <div className="flex flex-col gap-3 border-t pt-4">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-11 w-full" />
      </div>
    </DialogSkeleton>
  )
}
